import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { haversineMeters, ON_SITE_RADIUS_M, getBrowserPosition } from "@/lib/geoUtils";
import { toast } from "sonner";

export interface ActiveEntry {
  id: string;
  clock_in_at: string;
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_in_accuracy: number | null;
  confirmed_job_id: string | null;
  suggested_job_id: string | null;
  off_site_flag: boolean;
  off_site_reason: string | null;
  distance_from_job_m: number | null;
  job?: { id: string; property_address: string; latitude: number | null; longitude: number | null } | null;
}

export interface JobCandidate {
  id: string;
  property_address: string;
  latitude: number | null;
  longitude: number | null;
  distance_m: number | null;
}

interface FindSuggestedJobResult {
  best: JobCandidate | null;
  candidates: JobCandidate[];
  hasScheduledJobs: boolean;
}

interface FindSuggestedJobOptions {
  includeAllScheduledToday?: boolean;
}

const PING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useTimeClock() {
  const { user, orgId } = useAuth();
  const [activeEntry, setActiveEntry] = useState<ActiveEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pingTimerRef = useRef<number | null>(null);

  // Load current active entry
  const loadActive = useCallback(async () => {
    if (!user?.id || !orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("time_clock_entries")
      .select("id, clock_in_at, clock_in_lat, clock_in_lng, clock_in_accuracy, confirmed_job_id, suggested_job_id, off_site_flag, off_site_reason, distance_from_job_m, job:jobs!confirmed_job_id(id, property_address, latitude, longitude)")
      .eq("employee_id", user.id)
      .eq("org_id", orgId)
      .eq("status", "active")
      .order("clock_in_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) console.error("loadActive", error);
    setActiveEntry((data as any) || null);
    setLoading(false);
  }, [user?.id, orgId]);

  useEffect(() => {
    loadActive();
  }, [loadActive]);

  // Find best matching job for today using GPS
  const findSuggestedJob = useCallback(
    async (
      lat: number,
      lng: number,
      options: FindSuggestedJobOptions = {}
    ): Promise<FindSuggestedJobResult> => {
      if (!user?.id || !orgId) return { best: null, candidates: [], hasScheduledJobs: false };

      // Today as YYYY-MM-DD for the date column
      const today = new Date().toISOString().slice(0, 10);

      const { data: crews } = await supabase
        .from("crews")
        .select("id, foreman_id, members")
        .eq("org_id", orgId);

      const memberCrewIds = (crews || [])
        .filter((crew: any) => {
          if (crew.foreman_id === user.id) return true;

          if (!Array.isArray(crew.members)) return false;

          return crew.members.some((member: any) => {
            if (typeof member === "string") return member === user.id;
            if (member && typeof member === "object") {
              return member.id === user.id || member.user_id === user.id || member.profile_id === user.id;
            }
            return false;
          });
        })
        .map((crew: any) => crew.id);

      // Pull today's crew assignments scoped to the employee's crews first.
      let assignments: any[] = [];
      if (memberCrewIds.length > 0) {
        const { data } = await supabase
          .from("crew_assignments")
          .select("job_id, crew_id, job:jobs(id, property_address, latitude, longitude)")
          .eq("org_id", orgId)
          .eq("scheduled_date", today)
          .in("crew_id", memberCrewIds)
          .neq("status", "cancelled");
        assignments = data || [];
      }

      // Dispatch-board punching needs to work even when the employee is not yet linked
      // to a crew record, so fall back to all scheduled jobs for today in the org.
      if (assignments.length === 0 && options.includeAllScheduledToday) {
        const { data } = await supabase
          .from("crew_assignments")
          .select("job_id, crew_id, job:jobs(id, property_address, latitude, longitude)")
          .eq("org_id", orgId)
          .eq("scheduled_date", today)
          .neq("status", "cancelled");
        assignments = data || [];
      }

      const seen = new Set<string>();
      const all: JobCandidate[] = [];

      const push = (j: any) => {
        if (!j || !j.id || seen.has(j.id)) return;
        seen.add(j.id);
        const distance =
          j.latitude != null && j.longitude != null
            ? haversineMeters(lat, lng, Number(j.latitude), Number(j.longitude))
            : null;
        all.push({
          id: j.id,
          property_address: j.property_address,
          latitude: j.latitude,
          longitude: j.longitude,
          distance_m: distance,
        });
      };

      (assignments || []).forEach((a: any) => push(a.job));

      // Sort by distance (nulls last)
      all.sort((a, b) => {
        if (a.distance_m == null) return 1;
        if (b.distance_m == null) return -1;
        return a.distance_m - b.distance_m;
      });

      return { best: all[0] || null, candidates: all, hasScheduledJobs: all.length > 0 };
    },
    [user?.id, orgId]
  );

  // Send a single GPS ping for the active entry
  const sendPing = useCallback(async () => {
    if (!activeEntry?.id || !user?.id || !orgId) return;
    try {
      const pos = await getBrowserPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
      await supabase.from("time_clock_pings").insert({
        entry_id: activeEntry.id,
        org_id: orgId,
        employee_id: user.id,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });
    } catch (e) {
      console.warn("ping failed", e);
    }
  }, [activeEntry?.id, user?.id, orgId]);

  // Manage ping interval
  useEffect(() => {
    if (pingTimerRef.current) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (activeEntry?.id) {
      // first ping shortly after clock-in, then every 5 min
      const initial = window.setTimeout(() => sendPing(), 30000);
      pingTimerRef.current = window.setInterval(() => sendPing(), PING_INTERVAL_MS);
      return () => {
        window.clearTimeout(initial);
        if (pingTimerRef.current) window.clearInterval(pingTimerRef.current);
      };
    }
  }, [activeEntry?.id, sendPing]);

  // Clock in
  const clockIn = useCallback(
    async (params: {
      jobId: string;
      lat: number;
      lng: number;
      accuracy?: number;
      distanceM: number | null;
      offSite: boolean;
      offSiteReason?: string;
      source?: "mobile" | "dispatch";
    }) => {
      if (!user?.id || !orgId) throw new Error("Not signed in");
      setBusy(true);
      try {
        const { data, error } = await supabase
          .from("time_clock_entries")
          .insert({
            org_id: orgId,
            employee_id: user.id,
            confirmed_job_id: params.jobId,
            suggested_job_id: params.jobId,
            clock_in_lat: params.lat,
            clock_in_lng: params.lng,
            clock_in_accuracy: params.accuracy,
            distance_from_job_m: params.distanceM,
            off_site_flag: params.offSite,
            off_site_reason: params.offSiteReason || null,
            source: params.source || "mobile",
            status: "active",
          })
          .select("id")
          .single();
        if (error) throw error;

        // Note: crew_assignments are managed by dispatchers; clock-in just creates the time entry.

        toast.success(params.offSite ? "Clocked in (off-site flagged)" : "Clocked in");
        await loadActive();
        return data?.id;
      } finally {
        setBusy(false);
      }
    },
    [user?.id, orgId, loadActive]
  );

  // Clock out
  const clockOut = useCallback(async () => {
    if (!activeEntry?.id) return;
    setBusy(true);
    try {
      let lat: number | null = null;
      let lng: number | null = null;
      let acc: number | undefined;
      try {
        const pos = await getBrowserPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        acc = pos.coords.accuracy;
      } catch {
        // Allow clock-out without GPS
      }
      const { error } = await supabase
        .from("time_clock_entries")
        .update({
          clock_out_at: new Date().toISOString(),
          clock_out_lat: lat,
          clock_out_lng: lng,
          clock_out_accuracy: acc,
          status: "closed",
        })
        .eq("id", activeEntry.id);
      if (error) throw error;
      toast.success("Clocked out");
      await loadActive();
    } finally {
      setBusy(false);
    }
  }, [activeEntry?.id, loadActive]);

  return {
    activeEntry,
    loading,
    busy,
    findSuggestedJob,
    clockIn,
    clockOut,
    refresh: loadActive,
    isOnSite: ON_SITE_RADIUS_M,
  };
}
